import {api, isPaginatedQuery, PaginationQuery, PaginationResponse} from '@services/Api'
import {DEFAULT_POSTS_PER_PAGE} from "@lib/constants";
import {Issue} from "@mwServices/Issue";
import {transformMostValuableResponse} from "@mwServices/User";

export interface Box {
  id: number;
  name: string;
  description: number;
  artwork_id?: number | null;
  image_url?: string;
  issues?: Issue[];
  most_valuable?: Issue | null;
  issue_count?: number;
  fmv?: number | null;
  fmv_updated?: string | null;
  fmv_can_update?: boolean | null;
  fmv_is_stale?: boolean | null;
  overview?: {
    items: number;
    items_hot: number;
    items_key: number;
    items_rare: number;
  }
}

export interface BoxesResponse {
  boxes: Box[];
  pagination?: PaginationResponse;
}

export interface BoxesQuery{
  filters: {
    query?: string;
    sort?: number;
  },
}

const transformGetBoxResponse = (responseData: any) => {
  if(responseData.success !==1) return responseData;
  return {
    ...responseData,
    result: {
      ...responseData.result,
      most_valuable: transformMostValuableResponse(responseData.result.most_valuable),
      fmv: Number(responseData.result.fmv),
      fmv_can_update: (String(responseData.result.fmv_can_update) === '1'),
      fmv_is_stale: (String(responseData.result.fmv_is_stale) === '1'),
    }
  }
};
const transformGetBoxesResponse = (responseData) => {
  return {
    ...responseData,
    boxes: responseData.boxes.map(box => {
      const boxResponse = transformGetBoxResponse({success: 1, result: box});
      return boxResponse.result;
    })
  };
};

export interface BoxesPaginationQuery extends BoxesQuery, PaginationQuery{}

const boxesApi = api.injectEndpoints({
  endpoints: (build) => ({
    boxesAddBox: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/boxes/add`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Boxes','Overview'],
    }),
    boxesUpdateBox: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/boxes/update`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Boxes'],
    }),
    boxesRemoveBox: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/boxes/remove`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Boxes','Overview'],
    }),
    getBoxes: build.query<BoxesResponse, BoxesQuery | BoxesPaginationQuery>({
      query: (args) => {
        const filters = args ? encodeURIComponent(btoa(JSON.stringify(args.filters))) : '';

        if(isPaginatedQuery(args)) {
          const page = args ? args.page : 1;
          const perPage = args ? args.perPage : DEFAULT_POSTS_PER_PAGE;
          return {
            url: `covr/v1/user/boxes/all?filters=${filters}&page=${page}&per_page=${perPage}`,
            method: "GET",
          };
        }
        return {
          url: `covr/v1/user/boxes/all?filters=${filters}`,
          method: "GET",
        };
      },
      providesTags: ['Boxes'],
      transformResponse: transformGetBoxesResponse,
    }),
    getBoxById: build.query({
      query: (id) => {
        return {
          url: `covr/v1/user/boxes/get?box_id=${id}`,
          method: "GET",
        };
      },
      providesTags: ['Boxes'],
      transformResponse: transformGetBoxResponse,
    }),
    boxesAddIssues: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/boxes/add_issues`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Boxes','Issue','Overview'],
    }),
    boxesRemoveIssues: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/boxes/remove_issues`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Boxes','Issue','Overview'],
    }),
    boxesAddSeries: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/boxes/add_series`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Boxes','Issue','Overview'],
    }),
    refreshBoxFmv: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/boxes/refresh_value`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Boxes','Overview'],
    }),
    refreshAllBoxesFmv: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/boxes/refresh_value_all`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Boxes','Overview'],
    }),
    boxAssignLocation: build.mutation({
      query: (payload) => {
        return {
          url: `covr/v1/user/boxes/assign_location`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: ['Boxes'],
    }),
  }),
});

export const {
  useBoxesAddBoxMutation,
  useGetBoxesQuery,
  useBoxesRemoveBoxMutation,
  useBoxesUpdateBoxMutation,
  useGetBoxByIdQuery,
  useBoxesAddIssuesMutation,
  useBoxesRemoveIssuesMutation,
  useRefreshAllBoxesFmvMutation,
  useRefreshBoxFmvMutation,
  useBoxesAddSeriesMutation,
  useBoxAssignLocationMutation,
} = boxesApi;
